import * as XLSX from "xlsx";
import { DeliveryOrder } from "../types/misa";

/**
 * Các cột cố định theo đúng thứ tự như giao diện MISA:
 * STT | Ngày | Số phiếu | Tên KH | Tổng tiền | Trạng thái GH
 * | Thu hộ | Thông tin gói hàng | Phí GH trả ĐT | Kênh bán hàng | Ghi chú
 *
 * Mỗi cột định nghĩa: header tiếng Việt + danh sách field API có thể map (ưu tiên từ trên xuống)
 */
interface ColumnDef {
  header: string;
  fields: string[]; // thử lần lượt, lấy field đầu tiên có giá trị
  isNumber?: boolean;
  isDate?: boolean;
  isStatus?: boolean;
}

const DELIVERY_STATUS_MAP: Record<number, string> = {
  5: "Chờ giao/lấy hàng",
  6: "Đang giao hàng",
  7: "Thất bại",
  8: "Hoàn thành",
  9: "Đã chuyển hoàn",
  10: "Chờ thu COD",
};

const FIXED_COLUMNS: ColumnDef[] = [
  {
    header: "STT",
    fields: [],
  },
  {
    header: "Ngày tạo đơn",
    fields: ["CreatedDate"],
    isDate: true,
  },
  {
    header: "Ngày giao hàng",
    fields: ["DeliveryDate"],
    isDate: true,
  },
  {
    header: "Ngày hóa đơn",
    fields: ["CompleteInvoiceDate"],
    isDate: true,
  },
  {
    header: "Trạng thái",
    fields: ["RefStatus"],
    isStatus: true,
  },
  {
    header: "Hóa đơn",
    fields: ["RefNo"],
  },
  {
    header: "NV bán hàng",
    fields: ["EmployeeData"],
  },
  {
    header: "Người nhận",
    fields: ["Reciever"],
  },
  {
    header: "SĐT người nhận",
    fields: ["RecipientTel"],
  },
  {
    header: "Địa chỉ giao hàng",
    fields: ["DeliveryAddress"],
  },
  {
    header: "Phí GH thu khách",
    fields: ["DeliveryCost"],
    isNumber: true,
  },
  {
    header: "ĐT giao hàng",
    fields: ["Shippartner"],
  },
  {
    header: "Trạng thái ĐVVC",
    fields: ["PartnerStatusName"],
  },
  {
    header: "Mã vận đơn",
    fields: ["BillCode"],
  },
  {
    header: "Tổng thanh toán",
    fields: ["TotalCost"],
    isNumber: true,
  },
  {
    header: "Đặt cọc",
    fields: ["DepositAmount", "Deposit", "PrepaidAmount", "AdvanceAmount"],
    isNumber: true,
  },
  {
    header: "Khách nợ",
    fields: ["DebitAmount"],
    isNumber: true,
  },
  {
    header: "Còn phải thu",
    fields: ["RemainAmount"],
    isNumber: true,
  },
  {
    header: "Thu hộ",
    fields: [],
    isNumber: true,
  },
  {
    header: "Phí GH trả ĐT",
    fields: ["ShippingPartnerAmount"],
    isNumber: true,
  },
  {
    header: "Kênh bán hàng",
    fields: ["SaleChannel"],
  },
  {
    header: "Ghi chú",
    fields: ["Note"],
  },
];

function getFieldValue(row: DeliveryOrder, col: ColumnDef): any {
  for (const field of col.fields) {
    const val = row[field];
    if (val !== null && val !== undefined && val !== "") return val;
  }
  return "";
}

function formatValue(value: any, col: ColumnDef): any {
  if (value === null || value === undefined || value === "") return "";
  if (col.isStatus) {
    const code = typeof value === "number" ? value : Number(value);
    return DELIVERY_STATUS_MAP[code] ?? value;
  }
  if (col.isDate && typeof value === "string") {
    try {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
      }
    } catch {
      // fall through
    }
  }
  if (col.isNumber && typeof value === "number") return value;
  return value;
}

export function exportDeliveryOrdersToExcel(
  data: DeliveryOrder[],
  fromDate: string,
  toDate: string,
): void {
  if (!data || data.length === 0) {
    alert("Không có dữ liệu đơn giao hàng để xuất!");
    return;
  }

  const allCols = FIXED_COLUMNS;

  // Build header row
  const headerRow = allCols.map((c) => c.header);

  // Build data rows
  const dataRows = data.map((row, idx) =>
    allCols.map((col) =>
      col.header === "STT"
        ? idx + 1
        : formatValue(getFieldValue(row, col), col),
    ),
  );

  const worksheetData = [headerRow, ...dataRows];
  const ws = XLSX.utils.aoa_to_sheet(worksheetData);

  // Style header row
  const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
  for (let c = range.s.c; c <= range.e.c; c++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c });
    if (!ws[cellRef]) continue;
    ws[cellRef].s = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { patternType: "solid", fgColor: { rgb: "1976D2" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "CCCCCC" } },
        bottom: { style: "thin", color: { rgb: "CCCCCC" } },
        left: { style: "thin", color: { rgb: "CCCCCC" } },
        right: { style: "thin", color: { rgb: "CCCCCC" } },
      },
    };
  }

  // Auto column width
  ws["!cols"] = allCols.map((col) => {
    if (col.header === "STT") return { wch: 5 };
    const maxLen = Math.max(
      col.header.length,
      ...data.map((row) => String(getFieldValue(row, col) ?? "").length),
    );
    return { wch: Math.min(Math.max(maxLen + 2, 12), 45) };
  });

  // Freeze header row
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Đơn giao hàng");

  const fileName = `Don_giao_hang_${fromDate.replace(/-/g, "")}_${toDate.replace(/-/g, "")}.xlsx`;
  XLSX.writeFile(wb, fileName);
}
